import type { Issue } from '../types/issue';

// Hardcoded Releases — werden zusätzlich zu denen aus dem Release-Kalender
// immer erkannt. So bleibt die App auch dann benutzbar, wenn der Kalender
// noch nicht gepflegt ist.
const HARDCODED_RELEASES = ['2026 R2', '2027 R1', '2027 R2'] as const;

// Aktive Releases = hartcodierte Liste + alles aus dem Release-Kalender.
export function getKnownReleases(calReleaseNames: string[]): string[] {
  return Array.from(new Set([...HARDCODED_RELEASES, ...calReleaseNames]));
}

// Alle Fix-Version-Strings eines Items. JIRA exportiert mehrere
// gleichnamige Spalten als Array — wir liefern den vollen Array zurück.
export function fixVersionsOf(r: Issue | undefined): string[] {
  if (!r) return [];
  const keys = ['Fix versions', 'Fix Version', 'Fix Version/s', 'Fix version', 'Custom field (Fix Version)'];
  const out: string[] = [];
  for (const k of keys) {
    const v = (r as Record<string, unknown>)[k];
    if (Array.isArray(v)) {
      for (const x of v) {
        const s = String(x).trim();
        if (s) out.push(s);
      }
    } else if (typeof v === 'string' && v.trim()) {
      out.push(v.trim());
    }
  }
  return out;
}

// Erste Fix-Version (Legacy; ersatz durch fixVersionsOf für Multi-Match).
export function fixVersionOf(r: Issue | undefined): string {
  return fixVersionsOf(r)[0] ?? '';
}

// Erkennt "YYYY R<N>" in beliebigen Strings.
//   "2026 R2"          → 2026 R2
//   "2026 R2 SP1"      → 2026 R2
//   "Release 2026-R2"  → 2026 R2
// Suffixe nach R<N> (SP, RC, .1, Hotfix etc.) werden ignoriert.
// Liefert nur Treffer, die in den bekannten Releases existieren — sonst null.
export function matchKnownRelease(s: unknown, knownReleases: string[]): string | null {
  if (!s) return null;
  const str = String(s).trim();
  const m = str.match(/\b(20\d{2})\s*[._\s\-]*r\s*(\d+)\b/i);
  if (!m) return null;
  const candidate = `${m[1]} R${m[2]}`;
  return knownReleases.includes(candidate) ? candidate : null;
}

// Release eines Epics ermitteln. Priorität:
//   1) ALLE Fix Versions des Epics durchsuchen — erste, die zu einem
//      known Release matched, gewinnt. Wenn ein Item ["2025 R3","2026 R1"]
//      hat, wird 2026 R1 erkannt (sofern es in knownReleases steht).
//      Auch SP/RC/SPn-Suffixe werden vom Regex auf das Basis-Release
//      gemappt.
//   2) Parent summary / Parent key / Summary (Fallback)
//   sonst: "Ohne Release-Zuordnung"
export function detectRelease(epic: Issue, knownReleases: string[]): string {
  for (const fv of fixVersionsOf(epic)) {
    const m = matchKnownRelease(fv, knownReleases);
    if (m) return m;
  }
  const candidates = [
    (epic as Record<string, unknown>)['Parent summary'],
    epic['Parent key'],
    epic['Summary'],
  ];
  for (const c of candidates) {
    const m = matchKnownRelease(c, knownReleases);
    if (m) return m;
  }
  return 'Ohne Release-Zuordnung';
}

// Liefert alle Releases, denen das Item angehört. Wenn das Item z.B.
// Fix Versions ["2026 R1 SP1", "2026 R2"] hat, gehört es zu R1 UND R2.
// Wichtig für die Detail-/Ist-Berechnung: ein Item zählt zu beiden
// Release-Säulen, wenn es zu beiden gehört.
//
// Mit Parent-Fallback: wenn keine eigene Fix Version matched, wird das
// Release des Parent-Epics übernommen.
export function releasesOfIssue(
  issue: Issue,
  byKey: Record<string, Issue>,
  knownReleases: string[]
): string[] {
  const own = releasesOfIssueStrict(issue, knownReleases);
  if (own.length > 0) return own;
  const pk = issue['Parent key'];
  if (pk) {
    const parent = byKey[pk];
    if (parent) {
      const r = detectRelease(parent, knownReleases);
      if (r !== 'Ohne Release-Zuordnung') return [r];
    }
  }
  return [];
}

// Strikte Variante: liefert NUR Releases, die direkt aus den Fix Versions
// des Items selbst kommen. Kein Parent-Epic-Fallback.
// Nutzen wir für die Detail-/Ist-Säule, wenn man sagen will: 'die Story
// muss explizit das Release zeigen'.
export function releasesOfIssueStrict(
  issue: Issue,
  knownReleases: string[]
): string[] {
  const out: string[] = [];
  for (const fv of fixVersionsOf(issue)) {
    const m = matchKnownRelease(fv, knownReleases);
    if (m && !out.includes(m)) out.push(m);
  }
  return out;
}

// Release eines Child-Items (für Single-Release-Zuordnung, Legacy).
// Liefert das erste matching Release. Für Multi-Release-Items
// stattdessen releasesOfIssue() nutzen.
export function releaseOfIssue(
  issue: Issue,
  byKey: Record<string, Issue>,
  knownReleases: string[]
): string {
  const rs = releasesOfIssue(issue, byKey, knownReleases);
  return rs[0] ?? 'Ohne Release-Zuordnung';
}
